import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "../lib/api";
import { apiFail } from "../lib/p2";
import { Btn, Card, Field, Textarea } from "./kit";

export const INFO_KEYS = ["description", "audience", "prerequisites", "objectives"];
const pick = (info, lang) => Object.fromEntries(INFO_KEYS.map((k) => [k, info?.[lang]?.[k] || info?.en?.[k] || ""]));

// Shows course description/audience/prerequisites/objectives; `flat` is already resolved to one language.
export function CourseInfo({ info, lang, flat, testid = "course-info", compact = false }) {
  const { t } = useTranslation();
  const v = flat || pick(info, lang);
  if (!INFO_KEYS.some((k) => v[k])) return null;
  return (
    <div className={compact ? "space-y-1 text-sm" : "space-y-3"} data-testid={testid}>
      {v.description && <p className="whitespace-pre-wrap" data-testid={`${testid}-description`}>{v.description}</p>}
      {INFO_KEYS.slice(1).map((k) => v[k] && (
        <div key={k} data-testid={`${testid}-${k}`}><p className="text-xs font-semibold uppercase tracking-[0.12em] text-clay-strong">{t(`ci.${k}`)}</p><p className="whitespace-pre-wrap text-sm text-neutral-ink">{v[k]}</p></div>
      ))}
    </div>
  );
}

export function CourseInfoEditor({ course, onSaved }) {
  const { t } = useTranslation();
  const [f, setF] = useState(() => ({ en: pickOwn(course.info, "en"), ru: pickOwn(course.info, "ru") }));
  const set = (l, k, val) => setF({ ...f, [l]: { ...f[l], [k]: val } });
  const save = () => api.put(`/learning/editor/courses/${course.id}/info`, { info: f }).then((r) => { toast.success(t("common.saved")); onSaved?.(r.data); }).catch(apiFail(t));
  return (
    <Card className="space-y-3" data-testid="course-info-editor">
      <h2 className="font-bold">{t("ci.title")}</h2>
      <div className="grid gap-4 lg:grid-cols-2">{["en", "ru"].map((l) => (
        <fieldset key={l} className="space-y-2 rounded-2xl border p-3"><legend className="px-1 text-sm font-bold">{t(`lang.${l}`)}</legend>
          {INFO_KEYS.map((k) => <Field key={k} label={t(`ci.${k}`)} htmlFor={`ci-${l}-${k}`}><Textarea id={`ci-${l}-${k}`} lang={l} rows={k === "description" ? 4 : 3} data-testid={`ci-${l}-${k}`} value={f[l][k]} onChange={(e) => set(l, k, e.target.value)} /></Field>)}
        </fieldset>))}</div>
      <Btn testid="ci-save" onClick={save}>{t("ci.save")}</Btn>
    </Card>
  );
}

function pickOwn(info, l) { return Object.fromEntries(INFO_KEYS.map((k) => [k, info?.[l]?.[k] || ""])); }
